/**
 * Correlation ringbuffer + user-agent coarsening (DF-4).
 *
 * THE GAP THIS CLOSES: `x-correlation-id` travels end-to-end through the
 * backend and has sat in CORS `exposedHeaders` since P4-1 — and NO frontend
 * ever read it (DRIP-FEEDBACK-LOOP.md §3, measured). A support answer to
 * "what happened when you clicked save" therefore always started with an
 * archaeology exercise. This module is the cheapest honest fix: the API
 * clients drop every response's correlation id into a small ring, and the
 * report form (DF-9/DF-12) reads the head of the ring as
 * `kontext.correlationId`.
 *
 * WHY A RING AND NOT "the last id": the call the melder is reporting is often
 * not the LAST call — a failing save is followed by the notification poll that
 * succeeded. A handful of recent entries lets the intake pick the most recent
 * FAILED one first (see `letzteKorrelationsId`), which is the id an engineer
 * actually greps for.
 *
 * WHAT THIS DELIBERATELY IS NOT: telemetry. Nothing leaves the page unless the
 * melder submits a report and leaves the technique block on (invariant D-E) —
 * the ring is module state in the tab, never persisted, never sent on its own.
 * Node-free and side-effect-free at import, so it exports via
 * `@finavest/shared/client` like the rest of the portal contract.
 */
/**
 * Ring capacity. Ten covers "the failing call plus the chatter around it" on
 * every screen this estate has; anything larger starts to look like telemetry.
 */
const KAPAZITAET = 10;
const ring = [];
/**
 * An opaque path segment — long enough that it can only be a credential, a
 * token or an id, never a route word (review #362 blocker 1: the signing flow
 * puts a LIVE single-use token in the PATH, `/contract/sign/<token>`, and a
 * ring that stores it re-creates the secret in module state). 20+ url-safe
 * characters catches every token/uuid-ish segment this estate mints while
 * leaving real route words (`eingaben`, `verifikation`, …) untouched.
 */
const OPAKES_SEGMENT = /^[A-Za-z0-9_-]{20,}$/;
/**
 * Redact one request path: strip the query (a query can carry search text) and
 * replace every opaque segment with `:token`. Exported since the DF-4
 * follow-up, because the kontext's `route` (`window.location.pathname`) walks
 * in through the SAME door a ring path does: the signing flow keeps a LIVE
 * single-use token in the URL (review #362 blocker 1), and it is there whether
 * the ring remembered the call or the melder files a report FROM that page.
 * One implementation for every path a report can carry — the redacted shape is
 * still exactly what an engineer greps a route table for.
 *
 * THE BOUNDS OF THE GATE, so "redacted" is never read wider than it is
 * (review #379 MINOR-1). What it catches is exactly ONE shape: a single path
 * segment of 20+ url-safe characters — which covers everything this estate
 * mints (`randomToken(32)` = 43 base64url chars, UUIDs = 36). What it does NOT
 * catch: a dotted JWT-shaped segment (`.` is not in the class), an
 * e-mail-shaped segment, a token under 20 characters, and a `#fragment` (only
 * `?` is stripped — `location.pathname` never carries one, but a hand-built
 * string can). A future route that puts a credential in one of THOSE forms
 * widens this regex first, or it ships unredacted.
 */
export function redigierePfad(pfad) {
    const ohneQuery = pfad.split('?')[0] ?? pfad;
    return ohneQuery
        .split('/')
        .map((segment) => (OPAKES_SEGMENT.test(segment) ? ':token' : segment))
        .join('/');
}
/**
 * Remember one call. The API clients call this for EVERY response that carries
 * the header — failures above all, since those are the ids worth reporting.
 * Redaction happens here rather than trusted away at the call sites: one
 * implementation, two frontends, one gate — with the bounds `redigierePfad`
 * states, not a blanket "never". The correlation ID is the lookup key, the
 * path is only orientation.
 */
export function merkeKorrelation(eintrag) {
    ring.push({ ...eintrag, pfad: redigierePfad(eintrag.pfad) });
    if (ring.length > KAPAZITAET)
        ring.shift();
}
/** The remembered calls, NEWEST FIRST. A copy — callers cannot edit the ring. */
export function letzteKorrelationen() {
    return [...ring].reverse();
}
/**
 * The one id a report should carry: the most recent FAILED call if any is in
 * the ring (that is almost always the thing being reported), otherwise the
 * most recent call at all, otherwise null.
 */
export function letzteKorrelationsId() {
    const neueste = letzteKorrelationen();
    const fehler = neueste.find((e) => e.status === 0 || e.status >= 400);
    return (fehler ?? neueste[0])?.id ?? null;
}
/** Test seam: an empty ring is the only state a fresh tab has. */
export function leereKorrelationen() {
    ring.length = 0;
}
/**
 * Coarsen a user agent to "Familie/Betriebssystem" ("Chrome/Windows") — the
 * repro hint §6.2 wants, never the raw string (a raw UA is a fingerprint and
 * would put device-identifying data into a row that lives for years).
 *
 * Order matters twice and both are UA-history, not taste: Edge carries
 * "Chrome/" in its UA so it must be asked first, and every browser on iOS is
 * Safari's engine wearing a coat, so iOS wins over the browser family.
 * Unknown stays "Unbekannt" — an honest shrug beats a wrong class.
 */
export function browserklasse(userAgent) {
    const ua = userAgent.toLowerCase();
    const os = ua.includes('iphone') || ua.includes('ipad')
        ? 'iOS'
        : ua.includes('android')
            ? 'Android'
            : ua.includes('windows')
                ? 'Windows'
                : ua.includes('mac os')
                    ? 'macOS'
                    : ua.includes('linux')
                        ? 'Linux'
                        : 'Unbekannt';
    const familie = ua.includes('edg/')
        ? 'Edge'
        : ua.includes('firefox/')
            ? 'Firefox'
            : ua.includes('chrome/') || ua.includes('crios/')
                ? 'Chrome'
                : ua.includes('safari/')
                    ? 'Safari'
                    : 'Unbekannt';
    return `${familie}/${os}`;
}
